import React, { useState, useEffect } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import { userSettingsService } from '../../services/user/userSettingsService';
import OnboardingPage from '../onboarding/OnboardingPage';
import { CircularProgress, Box } from '@mui/material';

interface OnboardingGuardProps {
  children: React.ReactNode;
}

const OnboardingGuard: React.FC<OnboardingGuardProps> = ({ children }) => {
  const { currentUser } = useAuthContext();
  const [loading, setLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!currentUser) {
        console.log('🔓 [ONBOARDING-GUARD] Sem usuário, permitindo acesso');
        setLoading(false);
        setNeedsOnboarding(false);
        return;
      }

      console.log('🔍 [ONBOARDING-GUARD] Verificando status do onboarding:', {
        uid: currentUser.uid,
        email: currentUser.email,
        displayName: currentUser.displayName
      });

      try {
        // Verificar se há bypass no localStorage (para debugging)
        const skipOnboarding = localStorage.getItem('skipOnboarding');
        // Só a chave por UID evita que outro utilizador na mesma máquina herde onboarding "completo".
        const onboardingCompleted = localStorage.getItem('onboarding_completed_' + currentUser.uid);

        if (skipOnboarding === 'true' || onboardingCompleted === 'true') {
          console.log('✅ [ONBOARDING-GUARD] Onboarding marcado como completo no localStorage, permitindo acesso');
          setNeedsOnboarding(false);
          setLoading(false);
          return;
        }
        
        // Verificar se o usuário já completou o onboarding
        // A função isOnboardingComplete já faz todas as verificações necessárias
        const hasCompletedOnboarding = await userSettingsService.isOnboardingComplete(currentUser.uid);

        if (hasCompletedOnboarding) {
          console.log('✅ [ONBOARDING-GUARD] Onboarding completo, permitindo acesso');
          // Salvar no localStorage para evitar verificações futuras
          localStorage.setItem('onboarding_completed_' + currentUser.uid, 'true');
          setNeedsOnboarding(false);
        } else {
          console.log('⚠️ [ONBOARDING-GUARD] Onboarding incompleto, mostrando tela de configuração');
          setNeedsOnboarding(true);
        }
      } catch (error) {
        console.error('❌ [ONBOARDING-GUARD] Erro ao verificar onboarding:', error);
        // Em caso de erro de permissão, assumir que onboarding está completo
        // (evitar loop de erro)
        if (error instanceof Error && error.message.includes('permission')) {
          console.warn('⚠️ [ONBOARDING-GUARD] Erro de permissão, assumindo onboarding completo');
          localStorage.setItem('onboarding_completed_' + currentUser.uid, 'true');
          setNeedsOnboarding(false);
        } else {
          // Para outros erros, mostrar onboarding
          setNeedsOnboarding(true);
        }
      } finally {
        setLoading(false);
      }
    };

    checkOnboardingStatus();
  }, [currentUser?.uid]);

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}
      >
        <CircularProgress sx={{ color: 'white' }} size={60} />
      </Box>
    );
  }

  // Se não há usuário, permitir acesso (será redirecionado pelo ProtectedRoute)
  if (!currentUser) {
    return <>{children}</>;
  }

  // Se precisa de onboarding, mostrar tela de configuração
  if (needsOnboarding) {
    console.log('🎯 [ONBOARDING-GUARD] Mostrando tela de onboarding');
    return <OnboardingPage />;
  }

  // Onboarding completo, permitir acesso
  return <>{children}</>;
};

export default OnboardingGuard;
